import {
    fetchCampaignApplication,
    fetchCampaignMetadata,
    fetchCampaignApplicationTeamMembers,
    fetchCampaignApplicationCompletionStatus,
} from '~/services/campaign';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { useParams } from 'react-router-dom';
import TitlePanel from '~/components/CampaignApplicationDetail/TitlePanel';
import PrimaryPanel from '~/components/CampaignApplicationDetail/PrimaryPanel';
import StatusCards from '~/components/CampaignApplicationDetail/StatusCards';
import ApplicationHistory from '~/components/CampaignApplicationDetail/History';
import CscBlock from '~/components/CampaignApplicationDetail/CscBlock';
import FundDetailsModal from '~/components/CampaignDetail/modals/FundDetailsModal';
import { useQueries } from '@tanstack/react-query';
import React, { useContext, useEffect, useState } from 'react';
import { CampaignContext } from '~/context/CampaignProvider';
import Loader from '~/components/Loader';
import { toast } from 'react-hot-toast';
import { ITeamMembersResponse } from '~/types/campaigns';
import { ApplicationCompletionStatus } from '~/types/application-completion';
import { fetchSkyApiFundDetails, fetchSkyApiFundCustomFields } from '~/services/finance-services';
import { fillDummyApplicationData } from '~/utils/fillDummyApplicationData';

const CampaignApplicationDetail: React.FC = () => {
    const contextValue = useContext(CampaignContext);
    if (!contextValue) throw new Error('CampaignContext not found');
    
    const [state, campaignDispatch] = contextValue as unknown as [{ campaignData: any }, any];
    const campaignData = state.campaignData;
    const [isloading, setIsLoading] = useState<boolean>(false);
    const [teamMembers, setTeamMembers] = useState<ITeamMembersResponse | null>(null);
    const [completionStatus, setCompletionStatus] = useState<ApplicationCompletionStatus | null>(null);
    const [completionStatusKey, setCompletionStatusKey] = useState(0);
    const [selectedSections, setSelectedSections] = useState<string[]>([]);
    const [isFundModalOpen, setIsFundModalOpen] = useState(false);
    const [approvedFundId, setApprovedFundId] = useState<string | null>(null);

    const [notes2, setNotes2] = useState<unknown[]>([]);
    const [requestNotesText, setRequestNotesText] = useState<string>('');
    const [activityLogRefreshKey, setActivityLogRefreshKey] = useState(0);
    const [isFilling, setIsFilling] = useState(false);
    const [fillStep, setFillStep] = useState('');

    // Function to refresh the activity log after any data update
    const refreshActivityLog = () => {
        setActivityLogRefreshKey((prev) => prev + 1);
    };
    const results = useQueries({
        queries: [
            {
                queryKey: ['metadata'],
                queryFn: () => fetchCampaignMetadata(),
                staleTime: Infinity,
            },
        ],
    });

    const metadata = results[0].data;

    const { application_id } = useParams();

    const fetchData = async (fundId?: string) => {
        try {
            setIsLoading(true);
            const applicationData = await fetchCampaignApplication(Number(application_id));

            // The new endpoint returns a flat structure with all nested data
            const {
                campaign,
                campaign_id,
                patient,
                csc,
                campaign_request,
                notes,
                coordinator,
                campaign_approval, // Campaign approval by HHL staff (NOT application completion)
                ...applicationFields
            } = applicationData;

            // Add campaign ID back to application object for components that need it
            // (like ApplicationStatusTab which fetches completion status)
            const application = {
                ...applicationFields,
                campaign: campaign_id,
            };

            campaignDispatch({
                type: 'set_campaign',
                payload: {
                    campaign,
                    patient,
                    csc,
                    campaign_request,
                    notes,
                    campaignPhotoInfo: null,
                    coordinator,
                    financial_coordinator: null,
                    count: 0,
                    application,
                    campaign_approval, // Campaign approval data (ps_approved_date, finance_approved_date, etc)
                },
            });
            
            setIsLoading(false);

            // If fund_id was provided (from approval), show the fund details modal
            if (fundId) {
                setApprovedFundId(fundId);
                setIsFundModalOpen(true);
            }
            
            // Refresh activity log after data reload
            refreshActivityLog();
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load application details');
            setIsLoading(false);
        }
    };

    const fetchTeamMembersData = async () => {
        try {
            const teamMembersData = await fetchCampaignApplicationTeamMembers(Number(application_id));
            setTeamMembers(teamMembersData);
        } catch (error) {
            console.error('Error fetching team members:', error);
            // Don't show error toast - team members are optional/supplementary data
        }
    };

    const fetchCompletionStatusData = async (campaignId: number) => {
        try {
            const status = await fetchCampaignApplicationCompletionStatus(campaignId);
            setCompletionStatus(status);
        } catch (error) {
            console.error('Error fetching completion status:', error);
            // Don't show error toast - completion status is optional/supplementary data
        }
    };

    const refreshCompletionStatus = () => {
        setCompletionStatusKey((prev) => prev + 1);
    };
    
    const refreshTeamMembers = async () => {
        await fetchTeamMembersData();
        // Also refresh completion status since team members affect contact section completion
        refreshCompletionStatus();
    };

    const handleFillDummyData = async () => {
        const appId = campaignData?.application?.id;
        const campId = campaignData?.campaign?.id;
        const reqId = campaignData?.campaign_request?.id;

        if (!appId || !campId || !reqId) {
            toast.error('Missing application, campaign, or request ID');
            return;
        }

        setIsFilling(true);
        try {
            const result = await fillDummyApplicationData({
                applicationId: appId,
                campaignId: campId,
                campaignRequestId: reqId,
                metadata: metadata,
                onStep: setFillStep,
            });

            if (result.success) {
                toast.success('All sections filled with dummy data');
            } else {
                toast.error(`Some sections failed: ${result.errors.join(', ')}`);
            }

            // Refresh everything
            await fetchData();
            await fetchTeamMembersData();
            refreshCompletionStatus();
        } catch (err) {
            console.error('Error filling dummy data:', err);
            toast.error('Failed to fill dummy data');
        } finally {
            setIsFilling(false);
            setFillStep('');
        }
    };

    const handleSectionToggle = (sectionKey: string) => {
        if (sectionKey === 'all') {
            // Show all sections
            setSelectedSections([]);
        } else {
            setSelectedSections((prev) => {
                if (prev.includes(sectionKey)) {
                    // Deselect this section
                    return prev.filter((key) => key !== sectionKey);
                } else {
                    // Select this section
                    return [...prev, sectionKey];
                }
            });
        }
    };

    useEffect(() => {
        if (application_id) {
            fetchData();
            fetchTeamMembersData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [application_id]);

    useEffect(() => {
        // Fetch completion status once campaign data is loaded
        if (campaignData?.campaign?.id) {
            fetchCompletionStatusData(campaignData.campaign.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignData?.campaign?.id, completionStatusKey]);

    useEffect(() => {
        // Combine campaign request notes array with campaign message notes for unified history
        const reqNotes = (campaignData?.campaign_request?.request_notes || []) as unknown[];
        const msgNotes = (campaignData?.notes || []) as unknown[];
        setNotes2([...(reqNotes || []), ...(msgNotes || [])]);
        if (campaignData?.campaign_request?.notes) {
            setRequestNotesText(campaignData.campaign_request.notes);
        }
    }, [campaignData]);

    if (isloading) return <Loader />;

    const {
        campaign,
        patient,
        csc,
        campaign_request,
        coordinator,
        application,
        campaign_approval,
    } = campaignData || {};

    const patientName = patient?.person 
        ? `${patient.person.first_name} ${patient.person.last_name}`.trim()
        : 'Campaign Application';

    return (
        <>
            <Breadcrumb 
                pageName='Campaign Application'
                parentPage='Campaign Applications'
                parentPath='/campaign-applications'
                breadcrumbTitle={patientName}
            />

            <TitlePanel 
                campaign={campaign} 
                campaignRequest={campaign_request} 
                application={application}
                onApplicationApproved={fetchData}
                completionStatus={completionStatus}
            />

            {/* Fund Details Modal - shown after approval */}
            {approvedFundId && (
                <FundDetailsModal
                    isOpen={isFundModalOpen}
                    onClose={() => {
                        setIsFundModalOpen(false);
                        setApprovedFundId(null);
                    }}
                    fundId={approvedFundId}
                    onFetchFund={fetchSkyApiFundDetails}
                    onFetchCustomFields={fetchSkyApiFundCustomFields}
                    customTitle="Application Approved"
                    customMessage={`Fund ${approvedFundId} has been successfully created in Raiser's Edge.`}
                    campaignId={campaign?.id}
                />
            )}

            {import.meta.env.VITE_TEST_MODE === 'true' && !completionStatus?.overall_complete && (
                <div className='mb-7.5 flex items-center gap-3'>
                    <button
                        type='button'
                        onClick={handleFillDummyData}
                        disabled={isFilling}
                        className='rounded-full border-2 border-dashed border-[#949599] px-6 py-2 text-sm font-bold uppercase text-[#949599] transition-colors hover:border-black hover:text-black disabled:opacity-50'
                    >
                        Fill All Sections with Dummy Data
                    </button>
                    {isFilling && (
                        <div className='flex items-center gap-2 text-sm text-[#949599]'>
                            <svg className='h-4 w-4 animate-spin' viewBox='0 0 24 24' fill='none'>
                                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                            </svg>
                            {fillStep}
                        </div>
                    )}
                </div>
            )}

            <StatusCards
                completionStatus={completionStatus}
                selectedSections={selectedSections}
                onSectionToggle={handleSectionToggle}
            />

            <div className='grid grid-cols-12 gap-7.5'>
                <div className='col-span-12 xl:col-span-8'>
                    <PrimaryPanel
                        patient={patient}
                        metadata={metadata}
                        campaign={campaign}
                        campaignRequest={campaign_request}
                        coordinator={coordinator}
                        application={application}
                        campaignApproval={campaign_approval}
                        teamMembers={teamMembers}
                        completionStatus={completionStatus}
                        refreshCompletionStatus={refreshCompletionStatus}
                        refreshTeamMembers={refreshTeamMembers}
                        selectedSections={selectedSections}
                    />
                </div>
                <div className='col-span-12 xl:col-span-4'>
                    <CscBlock
                        csc={csc}
                        campaignRequestId={campaign_request?.id}
                        onReassign={() => fetchData()}
                    />
                    <div className='mt-7.5'>
                        <ApplicationHistory
                            notes={notes2 as any}
                            campaign={campaign}
                            setNotes={setNotes2 as any}
                            campaignRequest={campaign_request as any}
                            campaignRequestNotes={requestNotesText}
                            onRequestNotesUpdate={(updatedNotes) => setRequestNotesText(updatedNotes)}
                            campaignApproval={campaign_approval}
                            activityLogRefreshKey={activityLogRefreshKey}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default CampaignApplicationDetail;
